import { HttpErrorResponse } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, debounceTime, EMPTY, filter, map, merge, of, switchMap, tap } from "rxjs";
import { CartService } from "projects/shared/services/cart.service";
import { SelectRegionalCurrency } from "../../nav-regional/store/regional-setting.selectors";
import { SelectCart, SelectCartItems } from "./cart.selectors";
import { RegionalActions } from "../../nav-regional/store/regional-setting.actions";
import { ToastService } from "projects/shared/components/toaster/toaster.service";
import { CartActions } from "./cart.actions";

const getErrorMessage = (error: unknown, fallback = 'Something went wrong'): string => {
  const err = error as HttpErrorResponse;
  const msg = err?.error?.message ?? err?.message;
  const text = Array.isArray(msg) ? msg.join(', ') : msg;
  return typeof text === 'string' && text.trim() ? text : fallback;
};

@Injectable()
export class CartEffect {
  store = inject(Store);
  actions$ = inject(Actions);
  cartService = inject(CartService);
  toaster = inject(ToastService);
  currency$ = this.store.selectSignal(SelectRegionalCurrency);
  cartItems$ = this.store.selectSignal(SelectCartItems);
  cart$ = this.store.selectSignal(SelectCart);

  getCartEffect$ = createEffect(() => this.actions$.pipe(
    ofType(CartActions.initializeCart),
    switchMap(() => this.cartService.getCart(this.currency$()).pipe(
      switchMap(cart => [
        CartActions.getCartData({ cart }),
        CartActions.getCartProducts({ items: cart.products })
      ]),
      catchError((error) => of(
        CartActions.getCartFailure({ getCartFailure: getErrorMessage(error, 'Failed to load your cart') })
      ))
    ))
  ));

  cartEffect$ = createEffect(() =>
    merge(
      this.actions$.pipe(ofType(CartActions.initializeCart)),
      this.actions$.pipe(ofType(RegionalActions.loadData)),
      this.actions$.pipe(ofType(RegionalActions.updateData)),
    ).pipe(
      switchMap(() => {
        const currency = this.currency$();
        const cart = this.cart$();

        const cartNotExists = !cart;
        const currencyMismatch = cart && cart.currency !== currency;

        if (!cartNotExists && !currencyMismatch) {
          return EMPTY;
        }

        return this.cartService.getCart(currency).pipe(
          switchMap(res => [
            CartActions.getCartData({ cart: res }),
            CartActions.getCartProducts({ items: res.products })
          ]),
          catchError((error) => of(
            CartActions.getCartFailure({ getCartFailure: getErrorMessage(error, 'Failed to load your cart') })
          ))
        );
      })
    )
  );

  itemEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.addItem, CartActions.updateCartItem, CartActions.mergeCartItems),
      // Merge actions fired in the same tick into a single request
      debounceTime(0),
      switchMap(() =>
        this.cartService.addToCart(this.cartItems$()).pipe(
          switchMap(() => this.cartService.getCart(this.currency$())),
          switchMap(res => [
            CartActions.getCartData({ cart: res }),
            CartActions.getCartProducts({ items: res.products }),
            CartActions.updateCartSuccess({ updateCartSuccess: 'Cart updated successfully' })
          ]),
          catchError((error) => of(
            CartActions.updateCartFailure({ updateCartFailure: getErrorMessage(error, 'Failed to update your cart') })
          ))
        )
      )
    )
  );

  removeItemEffect$ = createEffect(() => this.actions$.pipe(
    ofType(CartActions.removeItem),
    switchMap(() => this.cartService.addToCart(this.cartItems$()).pipe(
      switchMap(() => this.cartService.getCart(this.currency$())),
      switchMap(res => [
        CartActions.updateCart({ cart: res }),
        CartActions.getCartProducts({ items: res.products }),
        CartActions.updateCartSuccess({ updateCartSuccess: 'Item removed from cart' })
      ]),
      catchError((error) => of(
        CartActions.updateCartFailure({ updateCartFailure: getErrorMessage(error, 'Failed to remove the item') })
      ))
    ))
  ));

  // Show the success toast, then clear the message from the store
  cartSuccessToast$ = createEffect(() => this.actions$.pipe(
    ofType(CartActions.updateCartSuccess),
    map(({ updateCartSuccess }) => updateCartSuccess),
    filter((message) => !!message?.trim()),
    tap((message) => this.toaster.success(message, { title: 'Success' })),
    map(() => CartActions.clearCartSuccess())
  ));

  // Show the error toast, then clear the error from the store
  cartErrorToast$ = createEffect(() => this.actions$.pipe(
    ofType(CartActions.getCartFailure, CartActions.updateCartFailure),
    map((action) =>
      'getCartFailure' in action ? action.getCartFailure : action.updateCartFailure
    ),
    filter((message) => !!message?.trim()),
    tap((message) => this.toaster.error(message)),
    map(() => CartActions.clearCartError())
  ));
}